// R-131-FIN-DECISION-E — transparent per-class WACC risk premiums.

import type {
  FuelPriceRisk,
  Pathway,
  RegulatoryRisk,
  TechnologyId,
  TechnologyMaturity,
  WaccPremiumComponent,
} from './types';

export interface RiskAdjustedWaccInput {
  baseWaccPct: number;
  technology: TechnologyId;
  technologyMaturity: TechnologyMaturity;
  regulatoryRisk: RegulatoryRisk;
  fuelPriceRisk: FuelPriceRisk;
  projectLifeYears: number;
}

export interface RiskAdjustedWaccResult {
  waccPct: number;
  premiumComponents: WaccPremiumComponent[];
}

function isGasTechnology(technology: TechnologyId): boolean {
  return technology === 'gas-cc' || technology === 'gas-sc';
}

function isNuclearTechnology(technology: TechnologyId): boolean {
  return technology === 'nuclear-smr' || technology === 'nuclear-large';
}

function isRenewableBessTechnology(technology: TechnologyId): boolean {
  return technology === 'solar-bess' || technology === 'wind-bess';
}

function hasFoakNuclearPremium(components: WaccPremiumComponent[]): boolean {
  return components.some((component) => component.bps === 200 && component.name === 'FOAK nuclear');
}

function hasFuelVolatilityPremium(components: WaccPremiumComponent[]): boolean {
  return components.some((component) => component.name === 'Fuel risk');
}

/** Technology-class premiums calibrated from public deals (NREL ATB / Lazard). */
function technologyPremiums(input: RiskAdjustedWaccInput): WaccPremiumComponent[] {
  const { technology, technologyMaturity } = input;

  if (technology === 'mixed') {
    return [
      {
        name: 'Fuel risk',
        // +75bps gas fuel-price-volatility premium. Source: Lazard LCOE+ v17 (2024)
        // cost-of-capital ranges, where gas peaker/CCGT financing carries a fuel-volatility
        // spread over zero-fuel renewables; consistent with NREL ATB 2024 conventional-gas
        // financing assumptions. Mirrors the standalone gas-cc/gas-sc premium below.
        bps: 75,
        rationale: 'Hybrid bridge gas leg — fuel volatility on the BTM peaking/CC segment.',
      },
      {
        name: 'Construction risk',
        // +25bps grid-interconnect construction-risk premium. Source: NREL ATB 2024
        // financing assumptions for transmission/substation tie-in CapEx; consistent with
        // FERC interconnection-queue delay data informing amortization-timing risk.
        // Mirrors the standalone grid-tariff premium below.
        bps: 25,
        rationale: 'Hybrid grid cutover leg — substation CapEx amortization uncertainty.',
      },
    ];
  }

  if (
    isNuclearTechnology(technology) &&
    technologyMaturity === 'first-of-kind'
  ) {
    return [
      {
        name: 'FOAK nuclear',
        // +200bps first-of-a-kind nuclear premium. Source: DOE Loan Programs Office (LPO)
        // FOAK risk guidance and NREL ATB 2024 advanced-nuclear financing assumptions, which
        // apply an elevated WACC adder for unproven licensing/construction/performance risk
        // relative to NOAK plants. ~2 percentage points is consistent with Lazard LCOE+ v17
        // commentary on first-of-a-kind nuclear cost-of-capital ranges.
        bps: 200,
        rationale: 'First-of-a-kind nuclear — licensing, construction, and performance risk premium.',
      },
    ];
  }

  if (isGasTechnology(technology) && technologyMaturity === 'mature') {
    return [
      {
        name: 'Fuel risk',
        // +75bps gas fuel-price-volatility premium. Source: Lazard LCOE+ v17 (2024)
        // cost-of-capital ranges for gas CCGT / simple-cycle versus zero-fuel renewables;
        // consistent with NREL ATB 2024 conventional-gas financing assumptions and EIA AEO
        // 2025 natural-gas price-volatility outlook.
        bps: 75,
        rationale: 'Mature gas CCGT / simple-cycle — fuel price volatility calibrated from public deals.',
      },
    ];
  }

  if (isRenewableBessTechnology(technology) && technologyMaturity === 'mature') {
    return [
      {
        name: 'Technology & financing',
        // +50bps technology & financing premium for solar/wind + storage. Source: NREL ATB
        // 2024 financing assumptions and Lazard LCOE+ v17 (2024) cost-of-capital ranges,
        // which apply a modest spread for paired-storage integration/performance risk over
        // standalone mature renewables with bankable offtake.
        bps: 50,
        rationale: 'Mature solar / wind + BESS — established technology with bankable offtake structures.',
      },
    ];
  }

  if (technology === 'grid-tariff' && technologyMaturity === 'mature') {
    return [
      {
        name: 'Construction risk',
        // +25bps grid-interconnect construction-risk premium. Source: NREL ATB 2024
        // financing assumptions for transmission/substation tie-in; informed by FERC
        // interconnection-queue delay data (COD-timing risk on amortization).
        bps: 25,
        rationale: 'Grid pathway — substation and tie-line CapEx amortization over queue-delayed COD.',
      },
    ];
  }

  return [];
}

/**
 * Sum per-class risk premiums transparently and return the adjusted WACC.
 * Cross-cutting premiums stack on top of the technology-class component(s).
 */
export function riskAdjustedWaccPct(input: RiskAdjustedWaccInput): RiskAdjustedWaccResult {
  const premiumComponents = technologyPremiums(input);

  if (
    input.regulatoryRisk === 'high' &&
    !hasFoakNuclearPremium(premiumComponents)
  ) {
    premiumComponents.push({
      name: 'Regulatory risk',
      // +50bps high-regulatory-uncertainty premium. Source: NREL ATB 2024 financing
      // assumptions (permitting/policy risk adders) and Lazard LCOE+ v17 (2024)
      // cost-of-capital ranges, which widen WACC for projects facing extended
      // permitting/licensing timelines.
      bps: 50,
      rationale: 'High regulatory uncertainty — extended permitting or licensing timelines.',
    });
  }

  if (
    input.fuelPriceRisk === 'high' &&
    !hasFuelVolatilityPremium(premiumComponents)
  ) {
    premiumComponents.push({
      name: 'Fuel price risk',
      // +75bps high fuel-price-exposure premium. Source: EIA AEO 2025 natural-gas
      // price-volatility outlook and Lazard LCOE+ v17 (2024) cost-of-capital ranges;
      // matches the technology-class gas fuel-risk adder for cases where exposure is not
      // already captured by the technology premium.
      bps: 75,
      rationale: 'High fuel-price exposure not captured in the technology-class premium.',
    });
  }

  if (input.projectLifeYears > 40) {
    premiumComponents.push({
      name: 'Terminal value uncertainty',
      // +50bps long-life terminal-value premium for project lives > 40 years. Source:
      // NREL ATB 2024 financing assumptions on long-lived assets and DOE QER guidance on
      // decommissioning/terminal-value uncertainty; the 40-year threshold reflects typical
      // financing-tenor horizons beyond which residual-value risk is priced in.
      bps: 50,
      rationale: 'Project life exceeds 40 years — terminal-value and decommissioning uncertainty.',
    });
  }

  const riskPremiumBps = premiumComponents.reduce((sum, component) => sum + component.bps, 0);

  return {
    waccPct: input.baseWaccPct + riskPremiumBps / 100,
    premiumComponents,
  };
}

/** Derive risk-adjusted WACC inputs from a pathway model object. */
export function riskAdjustedWaccFromPathway(pathway: Pathway): RiskAdjustedWaccResult {
  return riskAdjustedWaccPct({
    baseWaccPct: pathway.risk.baseWaccPct,
    technology: pathway.technology,
    technologyMaturity: pathway.risk.technologyMaturity,
    regulatoryRisk: pathway.risk.regulatoryRisk,
    fuelPriceRisk: pathway.risk.fuelPriceRisk,
    projectLifeYears: pathway.lifetime.designLifeYears,
  });
}

/** Sync `pathway.risk.riskPremiumBps` and premium breakdown from risk inputs. */
export function withSyncedRiskPremium(pathway: Pathway): Pathway {
  const { premiumComponents } = riskAdjustedWaccFromPathway(pathway);
  const riskPremiumBps = premiumComponents.reduce((sum, component) => sum + component.bps, 0);

  return {
    ...pathway,
    risk: {
      ...pathway.risk,
      riskPremiumBps,
      waccPremiumComponents: premiumComponents,
    },
  };
}

/** Human-readable WACC label for compare cards, e.g. "Gas CC WACC 8.5% (base 7.5% + 75bps fuel risk)". */
export function formatRiskAdjustedWaccLabel(
  pathwayLabel: string,
  baseWaccPct: number,
  result: RiskAdjustedWaccResult,
): string {
  const waccLabel = `${pathwayLabel} WACC ${result.waccPct.toFixed(1)}%`;
  if (result.premiumComponents.length === 0) {
    return `${waccLabel} (base ${baseWaccPct.toFixed(1)}%)`;
  }

  const premiumText = result.premiumComponents
    .map((component) => `${component.bps}bps ${component.name.toLowerCase()}`)
    .join(' + ');

  return `${waccLabel} (base ${baseWaccPct.toFixed(1)}% + ${premiumText})`;
}
